// Feature-flag names and the per-browser override helpers, with no dependencies
// beyond Browser.js. components/Features.js re-exports all of this for its existing
// importers; code that lives in the shared app chunk (containers/App.js and what it
// mounts, e.g. support-chat/SupportChatProvider.js, and hooks/useFeatureFlag.js)
// imports from HERE. Features.js also exports isAndroidOrIOSDevice, which pulls
// react-device-detect and ua-parser-js: 11.6 KB gzipped that landed in app.js for
// every page the first time a shared-chunk module imported a flag name from it
// (cca-app#798 bundle report, measured 2026-09-22).
import { isBrowser } from "./Browser";

export const LOGIN_WITH_FACEBOOK = "loginWithFacebook";
export const LOGIN_WITH_TWITTER = "loginWithTwitter";
export const LOGIN_WITH_EMAIL = "loginWithEmail";
export const NEW_DATE_SELECTOR = "newDateSelector";
export const RENDER_NEW_SCHEDULED_SECTION = "renderNewScheduledSection";
export const SHOW_BLOG_SWIPER = "showBlogSwiper";
export const DISMISS_CONTINUE_IN_APP = "dismissContinueInApp";
export const SPEAKER_NAMES = "speakerNames";
export const SUBSCRIPTION_DEBUGGING = "subscriptionDebugging";
export const STRIPE_CUSTOMER_ADMIN = "stripeCustomerAdmin";
export const ONE_PLAN = "onePlan";
export const CHAT_AI = "chatAI";
export const CHAT_AI_DEBUG_MODE = "chatAIDebugMode";
export const WORDS_ON_THE_STREET = "wordsOnTheStreet";
export const STREAM_AUDIO = "streamAudio";
// The call-page login gate (components/LoginGate.js): audio, transcript and slide
// deck ask an anonymous visitor to log in. Unlike the flags above this one is
// ALSO a PostHog flag -- the local value here is the developer override, the
// stage default lives in api-config.mjs (CALL_PAGE_LOGIN_GATE_DEFAULT).
export const CALL_PAGE_LOGIN_GATE = "callPageLoginGate";
// Support chat funnel mode and its proactive teaser (components/support-chat). Both
// are PostHog flags too; stage defaults live in api-config.mjs
// (SUPPORT_CHAT_FUNNEL_DEFAULT, SUPPORT_CHAT_TEASER_DEFAULT).
export const SUPPORT_CHAT_FUNNEL = "supportChatFunnel";
export const SUPPORT_CHAT_TEASER = "supportChatTeaser";
// The chat opening by itself a few seconds after a visitor lands (useAutoOpen in
// support-chat/SupportChatWidget.js). A PostHog flag too; stage default in
// api-config.mjs (SUPPORT_CHAT_AUTO_OPEN_DEFAULT).
export const SUPPORT_CHAT_AUTO_OPEN = "supportChatAutoOpen";
// Fetch the full transcript from the API after login even on a build that carries
// the whole text (TRANSCRIPT_PREVIEW_ONLY off -- a rollback rebuild, or a local
// GATSBY_TRANSCRIPT_PREVIEW_ONLY=false build). A PostHog flag, ramped like
// callPageLoginGate, so the runtime half of the preview build
// (src/transcriptPreview.mjs) can be exercised on real sessions without handing
// the text back to the page. On a preview-only build -- every stage's default --
// the fetch happens regardless: there is nothing else to show, and the flag
// decides nothing. Falls back to the in-page text on any failure.
export const TRANSCRIPT_FROM_API = "callPageTranscriptFromApi";
// The /api-pricing annual-default experiment (pages/api-pricing.js). A PostHog
// multivariate flag with two arms that share one card design and differ in two
// things: `control` lands on Monthly and shows Annual as the yearly total ("$576
// /Yearly"); `test` lands on Annual and shows it as a monthly equivalent ("$48
// /Month" with "Billed as $576/year").
// Read with useFeatureVariant, so `?pricing-annual-default=test` (or `=control`) pins
// an arm for that page load, and the same key in localStorage pins the browser; with
// no flag and no answer the visitor sees control.
// Kebab-case like the other experiment flag in PostHog (`pricing-trial-cta`).
export const PRICING_ANNUAL_DEFAULT = "pricing-annual-default";

export const isFeatureEnabled = (featureName) => {
  if (!isBrowser()) {
    return false;
  }
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(featureName)) {
    // Presence still means on, so a bare `?myFlag` keeps working -- but an explicit
    // `?myFlag=false` now means off. It used to mean ON, because only presence was
    // checked: the most natural way to write "turn this off" did the opposite. That
    // was survivable while the URL could only ever switch a flag on; it stopped being
    // so once isFeatureExplicitlySet let the URL settle a flag in BOTH directions,
    // where it would report the flag as explicitly set AND enabled.
    return searchParams.get(featureName) !== "false";
  }
  return readStoredFlag(featureName) === "true";
};

// localStorage can throw (a SecurityError under some privacy settings and in
// embedded contexts), and this runs on every page from the root provider: a
// flag that cannot be read is unset, one that cannot be written is dropped.
const readStoredFlag = (featureName) => {
  try {
    return localStorage.getItem(featureName);
  } catch (error) {
    return null;
  }
};
const writeStoredFlag = (featureName, value) => {
  try {
    localStorage.setItem(featureName, value);
  } catch (error) {
    // Best-effort: see above.
  }
};

/**
 * Has THIS browser taken a position on the flag, either way?
 *
 * `isFeatureEnabled` collapses "explicitly off" and "never set" into the same false,
 * which is fine when local is the only source of truth. Once PostHog can also answer
 * (see hooks/useFeatureFlag), the two have to be told apart: a developer who ran
 * `disableFeature` is overruling a rollout on purpose, and must not have PostHog turn
 * it back on under them.
 */
export const isFeatureExplicitlySet = (featureName) => {
  if (!isBrowser()) {
    return false;
  }
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(featureName)) {
    return true;
  }
  const stored = readStoredFlag(featureName);
  return stored === "true" || stored === "false";
};

export const enableFeature = (featureName) => {
  if (!isBrowser()) {
    return;
  }
  writeStoredFlag(featureName, "true");
};

export const disableFeature = (featureName) => {
  if (!isBrowser()) {
    return;
  }
  writeStoredFlag(featureName, "false");
};
